<template>
  <div class="flex-lg-1 h-screen overflow-y-lg-auto bg-light-soft">
    <!-- Header -->
    <header class="bg-white border-bottom py-6">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-md-7 col-12">
            <h1 class="h2 ls-tight fw-bold mb-1">
              <i class="bi bi-bar-chart-line text-primary me-2"></i>{{ pageTitle }} Management
            </h1>
            <p class="text-sm text-muted mb-0">
              Upload, register, and manage GWAS <code>.gz</code> summary statistics for downstream workflows.
            </p>
          </div>
          <div class="col-md-5 col-12 text-md-end mt-4 mt-md-0">
            <div class="d-flex gap-2 justify-content-md-end">
              <a
                :href="exampleLink"
                target="_blank"
                class="btn btn-sm btn-outline-primary rounded-pill px-4 shadow-none"
              >
                <i class="bi bi-file-earmark-arrow-down me-2"></i>Download Template
              </a>
              <button class="btn btn-sm btn-outline-secondary rounded-pill px-4 shadow-none" @click="fetchTableData">
                <i class="bi bi-arrow-clockwise me-2"></i>Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="py-10">
      <div class="container-fluid">
        <!-- Steps -->
        <div class="card shadow-sm border-0 rounded-4 mb-8">
          <div class="card-body p-6">
            <h6 class="text-uppercase text-muted ls-wide fw-bold text-xs mb-4">Current Status</h6>

            <div class="row g-0 text-center">
              <div class="col-md-4">
                <div class="d-flex flex-column align-items-center px-4">
                  <div :class="stepStatus.metadata ? 'bg-success' : 'bg-primary'" class="icon-shape text-white rounded-circle mb-3 shadow">
                    <i v-if="stepStatus.metadata" class="bi bi-check-lg"></i>
                    <span v-else>1</span>
                  </div>
                  <h6 class="fw-bold mb-1">Define Metadata</h6>
                  <p class="text-xs text-muted mb-0">Set display name and description.</p>
                </div>
              </div>

              <div class="col-md-4 border-start-md">
                <div class="d-flex flex-column align-items-center px-4">
                  <div
                    :class="uploadSuccess ? 'bg-success' : (isUploading ? 'bg-primary-animated' : (stepStatus.metadata ? 'bg-primary' : 'bg-light text-muted'))"
                    class="icon-shape text-white rounded-circle mb-3 shadow"
                  >
                    <i v-if="uploadSuccess" class="bi bi-check-lg"></i>
                    <div v-else-if="isUploading" class="spinner-border spinner-border-sm" role="status"></div>
                    <span v-else>2</span>
                  </div>
                  <h6 class="fw-bold mb-1">Stream Upload</h6>
                  <p class="text-xs text-muted mb-0">Upload <code>.gz</code> to PB storage.</p>
                </div>
              </div>

              <div class="col-md-4 border-start-md">
                <div class="d-flex flex-column align-items-center px-4">
                  <div :class="isRegistered ? 'bg-success text-white' : 'bg-light text-muted'" class="icon-shape rounded-circle mb-3 shadow">
                    <i v-if="isRegistered" class="bi bi-check-all"></i>
                    <span v-else>3</span>
                  </div>
                  <h6 class="fw-bold mb-1">Registered</h6>
                  <p class="text-xs text-muted mb-0">Indexed and visible in library.</p>
                </div>
              </div>
            </div>

            <!-- Guidance -->
            <div class="mt-5 alert border-0 mb-0 d-flex align-items-start bg-soft-primary text-primary rounded-4">
              <i class="bi bi-info-circle-fill me-3 fs-5 mt-1"></i>
              <div class="text-xs">
                <div class="fw-bold mb-1">Guidance</div>
                <ul class="mb-0 ps-3">
                  <li>Use a unique Display Name (preferably include trait + source + year).</li>
                  <li>Only <code>.gz</code> is accepted. Ensure it matches the expected GWAS schema.</li>
                  <li>After upload, the system registers the resource and maps it to a storage key.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload -->
        <div class="card shadow-sm border-0 rounded-4 mb-8">
          <div class="card-body p-8">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-4">
              <div class="d-flex align-items-center">
                <div class="icon-shape bg-primary-soft text-primary rounded-3 me-4">
                  <i class="bi bi-cloud-arrow-up-fill fs-4"></i>
                </div>
                <div>
                  <h4 class="mb-1 fw-bold">Upload New Dataset</h4>
                  <p class="text-sm text-muted mb-0">GWAS summary statistics (<code>.gz</code>), private scope.</p>
                </div>
              </div>

              <span class="badge rounded-pill bg-light text-muted px-3 py-2">
                Category: {{ type.toUpperCase() }}
              </span>
            </div>

            <div class="row g-4 mb-5">
              <div class="col-md-5">
                <label class="form-label fw-bold small text-uppercase text-muted">
                  Display Name
                  <el-tooltip content="A unique display name for this GWAS dataset." placement="top">
                    <i class="bi bi-question-circle text-muted ms-1"></i>
                  </el-tooltip>
                </label>
                <div class="input-group shadow-none">
                  <span class="input-group-text bg-light text-muted"><i class="bi bi-tag"></i></span>
                  <input
                    type="text"
                    class="form-control shadow-none"
                    placeholder="e.g. Height_GWAS_2024"
                    v-model.trim="fileName"
                    @blur="checkNameDuplicate"
                    :disabled="isUploading"
                  />
                </div>
                <div class="form-text text-xs text-muted">Avoid spaces; use underscores for consistency.</div>
              </div>

              <div class="col-md-7">
                <label class="form-label fw-bold small text-uppercase text-muted">
                  Description
                  <el-tooltip content="Optional notes (cohort, sample size, paper link, version)." placement="top">
                    <i class="bi bi-question-circle text-muted ms-1"></i>
                  </el-tooltip>
                </label>
                <div class="input-group shadow-none">
                  <span class="input-group-text bg-light text-muted"><i class="bi bi-pencil"></i></span>
                  <input
                    type="text"
                    class="form-control shadow-none"
                    placeholder="Study details, sample size, etc."
                    v-model.trim="description"
                    :disabled="isUploading"
                  />
                </div>
                <div class="form-text text-xs text-muted">Example: “UKB Europeans, N=350k, filtered to HapMap3”.</div>
              </div>
            </div>

            <div class="alert border-0 mb-5 d-flex align-items-start bg-light text-muted rounded-4">
              <i class="bi bi-lightbulb-fill me-3 fs-5 mt-1"></i>
              <div class="text-xs">
                <div class="fw-bold text-dark mb-1">Upload checklist</div>
                <ul class="mb-0 ps-3">
                  <li>File must end with <code>.gz</code>.</li>
                  <li><span style="color: red;">Ensure required columns match your pipeline expectations (e.g. CHR	SNP	POSITION	A1	A2	A1_FREQ	BETA	SE	P	N	N_EFF).</span></li>
                  <li>Large files may take longer(MAX size 500MB); avoid closing the page during upload.</li>
                </ul>
              </div>
            </div>

            <!-- Uploader gateway -->
            <div class="p-6 bg-light rounded-4 border-2 border-dashed border-primary-subtle position-relative">
              <div v-if="!stepStatus.metadata" class="text-center py-4">
                <p class="text-muted text-sm mb-0">
                  <i class="bi bi-lock-fill me-2"></i>Please enter a Display Name to unlock the uploader.
                </p>
              </div>

              <!-- inlined uploader -->
              <div v-show="stepStatus.metadata" class="rounded-4 border-2 border-dashed position-relative p-6 bg-white">
                <label class="position-absolute w-full h-full top-0 start-0 cursor-pointer" v-if="!isUploading">
                  <input
                    type="file"
                    class="visually-hidden"
                    accept=".gz"
                    ref="fileInput"
                    @change="onPickFile"
                    :disabled="isUploading"
                  />
                </label>

                <div class="text-center">
                  <div class="text-2xl text-muted">
                    <i v-if="!isUploading" class="bi bi-upload"></i>
                    <div v-else class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  </div>

                  <div class="d-flex text-sm mt-3 justify-content-center">
                    <p class="fw-semibold mb-0">
                      {{ isUploading ? "Uploading & Saving Path..." : "Click to upload GWAS .gz" }}
                    </p>
                  </div>

                  <div v-if="progressVisible" class="mt-4" style="min-width: 320px;">
                    <div class="d-flex align-items-center justify-content-center">
                      <span class="me-2 text-sm fw-bold" :class="statusText === 'failed' ? 'text-danger' : 'text-primary'">
                        {{ progress }}%
                      </span>
                      <div class="progress" style="width: 240px; height: 8px;">
                        <div
                          class="progress-bar progress-bar-striped progress-bar-animated"
                          :class="colorClass"
                          role="progressbar"
                          :style="`width:${progress}%`"
                        ></div>
                      </div>
                    </div>

                    <div class="text-xs text-muted mt-2 d-flex justify-content-center align-items-center">
                      <i class="bi bi-file-earmark-check me-1" v-if="statusText === 'successful'"></i>
                      {{ fileMeta.name }} ·
                      <span class="text-uppercase ms-1" :class="colorClass.replace('bg-', 'text-')">
                        {{ statusText }}
                      </span>
                    </div>

                    <div v-if="isUploading" class="mt-3 d-flex justify-content-center">
                      <button class="btn btn-sm btn-outline-secondary rounded-pill px-4" type="button" @click="cancelUpload">
                        <i class="bi bi-x-circle me-2"></i>Cancel Upload
                      </button>
                    </div>
                  </div>

                  <div v-if="uploadSuccess" class="mt-4 d-flex justify-content-center">
                    <div class="alert border-0 bg-soft-success text-success d-inline-flex align-items-start rounded-4 mb-0">
                      <i class="bi bi-check-circle-fill me-3 fs-5 mt-1"></i>
                      <div class="text-xs text-start">
                        <div class="fw-bold mb-1">Upload completed</div>
                        <div class="text-muted">
                          <div><span class="fw-semibold">Name:</span> {{ fileName }}</div>
                          <div><span class="fw-semibold">File:</span> {{ fileMeta.name }} ({{ prettyBytes(fileMeta.size) }})</div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              <!-- uploader end -->
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="card shadow-sm border-0 rounded-4">
          <div class="card-header bg-transparent border-bottom py-5">
            <div class="d-flex align-items-center justify-content-between gap-3">
              <div>
                <h5 class="mb-1 fw-bold">Manage Statistics Library</h5>
                <p class="text-sm text-muted mb-0">Browse and manage registered GWAS resources.</p>
              </div>
              <span class="badge bg-soft-primary text-primary rounded-pill px-3">{{ total }} Datasets</span>
            </div>

            <!-- Filters -->
            <div class="row g-2 mt-4">
              <div class="col-md-6">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
                  <input
                    type="text"
                    class="form-control shadow-none"
                    v-model.trim="keyword"
                    placeholder="Search by display name..."
                    @keyup.enter="fetchTableData"
                  />
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select form-select-sm shadow-none" v-model="statusFilter" @change="fetchTableData">
                  <option value="">All Status</option>
                  <option value="AVAILABLE">AVAILABLE</option>
                  <option value="UPLOADING">UPLOADING</option>
                  <option value="FAILED">FAILED</option>
                  <option value="REFRESHED">REFRESHED</option>
                </select>
              </div>
              <div class="col-md-3 d-flex justify-content-md-end gap-2">
                <button class="btn btn-sm btn-outline-secondary" @click="resetFilters">
                  <i class="bi bi-eraser me-1"></i>Reset
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="fetchTableData">
                  <i class="bi bi-arrow-repeat me-1"></i>Apply
                </button>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover table-nowrap align-middle">
              <thead class="table-light text-uppercase small fw-bolder text-muted">
                <tr>
                  <th class="ps-8 text-start">Resource & Key</th>
                  <th class="text-start">Upload Info</th>
                  <th class="text-start">Status</th>
                  <th class="text-start">Retention</th>
                  <th class="text-end pe-8">Operation</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in fileList" :key="file.file_id" class="table-row-hover">
                  <td class="ps-8">
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-sm bg-soft-primary text-primary rounded me-3">
                        <i class="bi bi-file-earmark-binary"></i>
                      </div>
                      <div class="min-w-0">
                        <span class="text-heading fw-bold d-block text-truncate">{{ file.display_name }}</span>
                        <code class="x-small text-muted font-monospace d-inline-block text-truncate" style="font-size: 0.7rem; max-width: 520px;">
                          {{ file.storage_key || file.identifier }}
                        </code>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div class="d-flex flex-column">
                      <span class="small text-dark">{{ file.create_time }}</span>
                      <span class="x-small text-muted">ID: {{ file.identifier }}</span>
                    </div>
                  </td>

                  <td>
                    <span :class="getStatusClass(file.status)">{{ file.status_label || file.status }}</span>
                  </td>

                  <td>
                    <div class="d-flex align-items-center">
                      <i class="bi bi-clock-history me-2 text-danger opacity-70"></i>
                      <span class="small text-danger fw-medium">{{ file.expire_time || "N/A" }}</span>
                    </div>
                  </td>

                  <td class="text-end pe-8">
                    <div class="d-flex justify-content-end gap-2">
                      <button
                        class="btn btn-sm btn-neutral"
                        :disabled="file.status === 'REFRESHED' || actionLoadingId === file.file_id"
                        @click="handleRefreshTTL(file.file_id)"
                        title="Extend retention"
                      >
                        <i class="bi bi-arrow-clockwise"></i>
                      </button>

                      <button
                        class="btn btn-sm btn-square btn-neutral text-danger-hover"
                        :disabled="actionLoadingId === file.file_id"
                        @click="handleDelete(file.file_id)"
                        title="Delete"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="fileList.length === 0">
                  <td colspan="5" class="text-center py-20 text-muted">
                    <div class="d-flex flex-column align-items-center">
                      <img
                        src="https://illustrations.popsy.co/gray/uploading-files.svg"
                        style="height: 110px;"
                        class="mb-3 opacity-50"
                        alt="empty"
                      />
                      <div class="fw-semibold mb-1">No GWAS datasets found</div>
                      <div class="text-xs text-muted">Try adjusting filters or upload a new file.</div>
                    </div>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>

          <div class="card-footer bg-transparent border-0 py-5">
            <div class="d-flex justify-content-center">
              <el-pagination
                @current-change="handlePageChange"
                :current-page="currentPage"
                :background="true"
                layout="total, prev, pager, next"
                :total="total"
                :page-size="pageSize"
                class="modern-pagination"
              />
            </div>
          </div>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import { listFiles, registerFile, deleteFileResource, refreshFileTTL, uploadFileRecord } from "@/api/storage";
import { isEmpty } from "@/utils/validate";
import { pb } from "@/utils/pocketbase";
import { MessageBox } from "element-ui";

export default {
  name: "GWASSummaryStatistics",
  data() {
    return {
      pageTitle: "GWAS Summary Statistics",
      exampleLink: "https://github.com/xinyu-c9/PGS-hub/raw/main/GWAS_summary_statistic_example/GWAS_summary_statistic_example.gz",

      fileList: [],
      type: "GWAS",

      fileName: "",
      description: "",

      total: 0,
      pageSize: 10,
      currentPage: 1,

      keyword: "",
      statusFilter: "",

      isUploading: false,
      isRegistered: false,
      uploadSuccess: false,

      progress: 0,
      progressVisible: false,
      colorClass: "bg-primary",
      statusText: "",
      fileMeta: { name: "", size: 0 },

      uploadController: null,
      maxFileBytes: 2 * 1024 * 1024 * 1024,

      // ✅ 防止重复点击，并让你知道是否进入了 handler
      actionLoadingId: null,
    };
  },
  computed: {
    stepStatus() {
      return {
        metadata: !isEmpty(this.fileName),
        upload: this.isUploading || this.uploadSuccess,
        index: this.isRegistered,
      };
    },
  },
  methods: {
    safeResetInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    prettyBytes(bytes) {
      if (!bytes && bytes !== 0) return "-";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let v = bytes;
      while (v >= k && i < sizes.length - 1) {
        v /= k;
        i++;
      }
      return `${v.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
    },

    resetFilters() {
      this.keyword = "";
      this.statusFilter = "";
      this.currentPage = 1;
      this.fetchTableData();
    },

    async fetchTableData() {
      try {
        const query = { category: this.type, page_size: this.pageSize, page_number: this.currentPage };
        const response = await listFiles(query);

        if (response.code === 0) {
          let list = response.data.file_list || [];

          const kw = (this.keyword || "").trim().toLowerCase();
          if (kw) list = list.filter((x) => String(x.display_name || "").toLowerCase().includes(kw));
          if (this.statusFilter) list = list.filter((x) => String(x.status || "").toUpperCase() === this.statusFilter);

          this.fileList = list;
          this.total = response.data.total_count || 0;
        } else {
          this.$message.error(response.message || "Failed to load table data");
        }
      } catch (error) {
        this.$message.error(error?.message || "Failed to load table data");
      }
    },

    checkNameDuplicate() {
      if (!isEmpty(this.fileName) && this.fileList.some((f) => f.display_name === this.fileName)) {
        this.$message.warning("This name is already in use (current page).");
      }
    },

    validateBeforeUpload(file) {
      if (!file) return { ok: false, msg: "No file selected." };
      if (isEmpty(this.fileName)) return { ok: false, msg: "Please enter Display Name first!" };
      if (!pb.authStore.isValid) return { ok: false, msg: "Session expired. Please login again." };

      const fname = String(file.name || "").toLowerCase();
      if (!fname.endsWith(".gz")) return { ok: false, msg: "Only .gz files are supported." };

      if (file.size > this.maxFileBytes) {
        this.$message.warning(`Large file (${this.prettyBytes(file.size)}). Upload may be slow depending on gateway limits.`);
      }
      return { ok: true };
    },

    cancelUpload() {
      try {
        if (this.uploadController) this.uploadController.abort();
      } catch {}
      this.isUploading = false;
      this.uploadSuccess = false;
      this.isRegistered = false;
      this.colorClass = "bg-danger";
      this.statusText = "cancelled";
      this.$message.warning("Upload cancelled.");
      this.safeResetInput();
    },

    async onPickFile(e) {
  const file = e.target.files?.[0];
  const v = this.validateBeforeUpload(file);
  if (!v.ok) {
    this.$message.error(v.msg);
    this.safeResetInput();
    return;
  }

  this.fileMeta = { name: file.name, size: file.size };
  this.isUploading = true;
  this.isRegistered = false;
  this.uploadSuccess = false;

  this.progressVisible = true;
  this.progress = 0;
  this.statusText = "uploading";
  this.colorClass = "bg-primary";

  this.uploadController = new AbortController();

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("owner", pb.authStore.model.id);
    formData.append("kind", "gwas");
    formData.append("scope", "private");
    formData.append("name", this.fileName);

    formData.append("bucket", "pocketbase");
    formData.append("key", "pending");

    formData.append("size_bytes", String(file.size));
    formData.append("status", "available");
    formData.append("content_type", file.type || "application/octet-stream");

    // 直接把注册信息一并传过去
    formData.append("display_name", this.fileName);
    formData.append("description", this.description || "");
    formData.append("file_category", this.type);
    formData.append("suffix", ".gz");

    const tags = {
      original_filename: file.name,
      uploaded_at: new Date().toISOString(),
      category: "GWAS",
      displayName: this.fileName,
      description: this.description,
    };
    formData.append("tags", JSON.stringify(tags));

    const record = await uploadFileRecord(
      formData,
      (evt) => {
        if (typeof evt === "number") {
          this.progress = Math.floor(evt);
        } else if (evt?.total) {
          this.progress = Math.floor((evt.loaded / evt.total) * 100);
        }
      },
      this.uploadController.signal
    );

    if (!record || !record.id) {
      throw new Error("Upload succeeded but record.id is missing");
    }

    this.progress = 100;
    this.statusText = "successful";
    this.colorClass = "bg-success";
    this.isUploading = false;
    this.uploadSuccess = true;
    this.isRegistered = true;

    this.$message.success("Successfully uploaded and indexed");

    setTimeout(() => {
      this.resetState();
      this.fetchTableData();
    }, 500);
  } catch (err) {
    this.isUploading = false;
    this.uploadSuccess = false;
    this.isRegistered = false;

    this.colorClass = "bg-danger";
    this.statusText = err?.name === "AbortError" ? "cancelled" : "failed";

    this.$message.error(`Upload Failed: ${err?.message || "unknown error"}`);
  } finally {
    this.safeResetInput();
  }
},

    async registerAfterUpload(record) {
      const payload = {
        display_name: this.fileName,
        description: this.description,
        file_category: this.type,
        storage_path: record.id,
        identifier: record.id,
        suffix: ".gz",
      };

      const res = await registerFile(payload);
      if (res.code === 0) {
        this.isRegistered = true;
        this.$message.success("Successfully indexed to storage");

        setTimeout(() => {
          this.resetState();
          this.fetchTableData();
        }, 500);
      } else {
        this.$message.error(res.message || "Registration failed");
        throw new Error(res.message || "Registration failed");
      }
    },

    resetState() {
      this.fileName = "";
      this.description = "";
      this.isUploading = false;
      this.isRegistered = false;
      this.uploadSuccess = false;

      this.progress = 0;
      this.progressVisible = false;
      this.colorClass = "bg-primary";
      this.statusText = "";
      this.fileMeta = { name: "", size: 0 };

      this.uploadController = null;
    },

    async handleRefreshTTL(id) {
      this.actionLoadingId = id;
      try {
        const res = await refreshFileTTL(id);
        if (res.code === 0) {
          this.$message.success("Retention extended");
          await this.fetchTableData();
        } else {
          this.$message.error(res.message || "Failed to extend retention");
        }
      } catch (err) {
        this.$message.error(err?.message || "Failed to extend retention");
      } finally {
        this.actionLoadingId = null;
      }
    },

    async handleDelete(id) {
      this.actionLoadingId = id;
      try {
        await MessageBox.confirm("Permanently delete?", "Warning", {
          type: "warning",
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          closeOnClickModal: false,
        });

        // ✅ 到这里一定会进入 delete，并产生请求
        const res = await deleteFileResource(id);

        if (res.code === 0) {
          this.$message.success("Resource purged");
          await this.fetchTableData();
        } else {
          this.$message.error(res.message || "Delete failed");
        }
      } catch (err) {
        // 用户取消：Element UI 会 reject
        if (err === "cancel" || err === "close") return;

        // ✅ 不再吞错，方便你定位
        this.$message.error(err?.message || "Delete failed (exception)");
      } finally {
        this.actionLoadingId = null;
      }
    },

    getStatusClass(status) {
      const base = "badge badge-lg rounded-pill ";
      const s = String(status || "").toUpperCase();
      if (s === "AVAILABLE" || s === "ACTIVE" || s === "FINISHED" || s === "SUCCESS") return base + "bg-soft-success text-success";
      if (s === "REFRESHED") return base + "bg-soft-primary text-primary";
      if (s === "FAILED") return base + "bg-soft-danger text-danger";
      if (s === "UPLOADING") return base + "bg-soft-secondary text-muted";
      return base + "bg-soft-secondary text-muted";
    },

    handlePageChange(val) {
      this.currentPage = val;
      this.fetchTableData();
    },
  },
  mounted() {
    this.fetchTableData();
  },
};
</script>

<style scoped>
.bg-light-soft { background-color: #f9fbfd; min-height: 100vh; }

/* shapes */
.icon-shape { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bg-primary-soft { background-color: rgba(121, 108, 255, 0.1); }

.bg-soft-success { background-color: #d1fae5; color: #065f46; }
.bg-soft-primary { background-color: #e0e7ff; color: #3730a3; }
.bg-soft-secondary { background-color: #f3f4f6; color: #6b7280; }
.bg-soft-danger { background-color: #fee2e2; color: #991b1b; }

.bg-primary-animated { background-color: #796CFF; animation: pulse 2s infinite; }
@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.6; } 100% { opacity: 1; } }

/* cursor + overlay label */
.cursor-pointer { cursor: pointer; }
.w-full { width: 100%; }
.h-full { height: 100%; }

/* row hover */
.table-row-hover:hover { background-color: #fcfdfe !important; transition: background 0.2s; }

/* truncate helpers */
.min-w-0 { min-width: 0; }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>